/**
 * Profile menu — the avatar dropdown (2026-08-12 profile-menu audit).
 *
 * Before this, the avatar opened the full settings modal directly:
 * signing out took two clicks through a modal, admin surfaces were
 * invisible from the identity surface, and the trigger's ▼ chevron
 * promised a menu that didn't exist. This IS that menu — deliberately
 * short (identity + 3 personal links + role-gated workspace links +
 * theme + sign out), routing into the surfaces that already exist:
 * the personal settings modal and the admin phases.
 *
 * Deliberately NOT here (junk-drawer guard): help/docs/changelog
 * (HelpMenu owns those), notification content (the bell), anything
 * content-shaped, integrations (admin phase, low frequency), language
 * (no i18n yet — a dead item is worse than none).
 *
 * Same open/close idiom as HelpMenu (outside click + Escape, aria
 * attrs); focus returns to the trigger on Escape.
 */

import React, { useEffect, useRef, useState } from 'react';

const itemStyle = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  gap: 10, width: '100%', textAlign: 'left',
  padding: '8px 10px', borderRadius: 8,
  background: 'transparent', border: 'none', cursor: 'pointer',
  fontSize: 13.5, color: 'var(--room-text-strong, #0f172a)',
  fontFamily: 'inherit',
};

const captionStyle = {
  fontSize: 10.5, fontWeight: 700, letterSpacing: '0.07em',
  textTransform: 'uppercase', color: 'var(--room-text-muted, #94a3b8)',
  padding: '6px 10px 2px',
};

const dividerStyle = {
  height: 1, background: 'var(--room-border, #e2e8f0)', margin: '6px 4px',
};

function MenuItem({ onClick, children, danger = false }) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      style={{ ...itemStyle, ...(danger ? { color: '#dc2626' } : {}) }}
      onMouseEnter={(e) => { e.currentTarget.style.background = danger ? 'rgba(220,38,38,0.06)' : 'var(--room-border-soft, #f8fafc)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
    >
      {children}
    </button>
  );
}

export default function ProfileMenu({
  authUser,
  theme,
  onSetTheme,
  onOpenSettingsTab,   // (tabId) => void — opens the personal settings modal
  onNavigateAdmin,     // ('security' | 'settings' | 'billing') => void
  onLogout,
  isPlaceholderCompany = false,
}) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (
        triggerRef.current && !triggerRef.current.contains(e.target)
        && menuRef.current && !menuRef.current.contains(e.target)
      ) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setOpen(false);
        // Return focus to the trigger so keyboard users aren't dropped
        // into the void when the menu vanishes.
        if (triggerRef.current) triggerRef.current.focus();
      }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!authUser) return null;

  const isAdmin = !!(authUser.company && authUser.company.isAdmin);
  const displayName = authUser.firstName
    ? `${authUser.firstName}${authUser.lastName ? ` ${authUser.lastName}` : ''}`
    : (authUser.fullName || authUser.username);
  const initial = (authUser.firstName || authUser.username || '?').charAt(0).toUpperCase();

  // Close-then-act wrapper so every item dismisses the menu.
  const act = (fn) => () => { setOpen(false); fn(); };

  return (
    <div className="profile-container" style={{ position: 'relative' }}>
      {/* Trigger — same visual as before (avatar + name + company),
          but the chevron now tells the truth: it opens a menu. */}
      <button
        ref={triggerRef}
        className="profile-trigger"
        type="button"
        onClick={() => setOpen((v) => !v)}
        title={displayName}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        {authUser.profilePictureUrl ? (
          <img src={authUser.profilePictureUrl} alt="" className="profile-trigger-avatar-img" />
        ) : (
          <div className="profile-avatar">{initial}</div>
        )}
        <div className="profile-trigger-info">
          <span className="profile-trigger-name">{authUser.firstName || authUser.username}</span>
          {isPlaceholderCompany ? (
            <span className="profile-trigger-company" style={{ fontStyle: 'italic', color: '#b45309' }}>
              Set up company
            </span>
          ) : (
            <span className="profile-trigger-company">{authUser.companyName}</span>
          )}
        </div>
        <span className="profile-trigger-arrow">{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div
          ref={menuRef}
          role="menu"
          aria-label="Account menu"
          style={{
            position: 'absolute', top: 'calc(100% + 6px)', right: 0,
            minWidth: 264,
            background: 'var(--room-surface, #ffffff)',
            border: '1px solid var(--room-border, #e2e8f0)',
            borderRadius: 10,
            boxShadow: '0 8px 24px rgba(15, 23, 42, 0.12)',
            padding: 6, zIndex: 60, fontFamily: 'inherit',
          }}
        >
          {/* Identity header — not clickable; answers "who am I signed
              in as" including the email, which previously appeared
              nowhere in the product. */}
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '8px 10px 10px' }}>
            {authUser.profilePictureUrl ? (
              <img src={authUser.profilePictureUrl} alt="" style={{ width: 34, height: 34, borderRadius: '50%', objectFit: 'cover' }} />
            ) : (
              <div className="profile-avatar" aria-hidden="true">{initial}</div>
            )}
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--room-text-strong, #0f172a)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {displayName}
              </div>
              <div style={{ fontSize: 12, color: 'var(--room-text-muted, #64748b)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {authUser.email}{authUser.companyName && !isPlaceholderCompany ? ` · ${authUser.companyName}` : ''}
              </div>
            </div>
          </div>
          <div style={dividerStyle} />

          {/* Personal settings — each opens the existing modal on the
              right tab (the modal remains the settings surface; this
              menu is just the router). */}
          <MenuItem onClick={act(() => onOpenSettingsTab('profile'))}>
            <span>Profile &amp; preferences</span>
          </MenuItem>
          <MenuItem onClick={act(() => onOpenSettingsTab('password'))}>
            <span>Password &amp; security</span>
          </MenuItem>
          <MenuItem onClick={act(() => onOpenSettingsTab('notifications'))}>
            <span>Notifications</span>
          </MenuItem>

          {/* Workspace — role-gated. An AE never sees this section;
              an admin finally has a path to the org surfaces from the
              identity menu (they were left-nav-only knowledge before). */}
          {isAdmin && (
            <>
              <div style={dividerStyle} />
              <div style={captionStyle}>Workspace</div>
              <MenuItem onClick={act(() => onNavigateAdmin('security'))}>
                <span>Team &amp; permissions</span>
              </MenuItem>
              <MenuItem onClick={act(() => onNavigateAdmin('billing'))}>
                <span>Billing &amp; plan</span>
              </MenuItem>
              <MenuItem onClick={act(() => onNavigateAdmin('settings'))}>
                <span>Workspace settings</span>
              </MenuItem>
            </>
          )}

          <div style={dividerStyle} />

          {/* Theme — inline toggle, no navigation. Kept as the one
              "quick pref" because it's the only preference people
              flip repeatedly. */}
          <div style={{ ...itemStyle, cursor: 'default' }} role="none">
            <span>Theme</span>
            <div style={{ display: 'inline-flex', gap: 4 }} role="group" aria-label="Theme">
              {['light', 'dark'].map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => onSetTheme(t)}
                  aria-pressed={theme === t}
                  style={{
                    padding: '3px 10px', borderRadius: 6, fontSize: 12, cursor: 'pointer',
                    fontWeight: theme === t ? 700 : 400,
                    border: `1px solid ${theme === t ? 'var(--room-accent, #6366f1)' : 'var(--room-border, #e2e8f0)'}`,
                    background: theme === t ? 'rgba(99, 102, 241, 0.10)' : 'transparent',
                    color: theme === t ? 'var(--room-accent, #6366f1)' : 'var(--room-text-muted, #64748b)',
                  }}
                >
                  {t === 'light' ? 'Light' : 'Dark'}
                </button>
              ))}
            </div>
          </div>

          <div style={dividerStyle} />
          <MenuItem danger onClick={act(onLogout)}>
            <span>Log out</span>
          </MenuItem>
        </div>
      )}
    </div>
  );
}
